import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  ChevronLeft,
  ChevronRight,
  Person,
} from '@mui/icons-material';
import TabbedResults from './TabbedResults';
import { searchKnowledgeCore } from '../services/api';
import { ViewEnrichmentRowStatus, SearchResult, SearchFormData } from '../types';

// ── Props ─────────────────────────────────────────────────────────────────────

interface EnrichmentResultsPageProps {
  viewName: string;
  rows: ViewEnrichmentRowStatus[];   // completed enrichment rows (name + address)
  onBack: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const EnrichmentResultsPage: React.FC<EnrichmentResultsPageProps> = ({
  viewName,
  rows,
  onBack,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentRow = rows[currentIndex];

  const loadResult = useCallback(async (row: ViewEnrichmentRowStatus) => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const formData: SearchFormData = {
        FIRST_NAME: row.first_name || '',
        LAST_NAME:  row.last_name,
        STREET1:    row.street || '',
        STREET2:    '',
        CITY:       row.city  || '',
        STATE:      row.state || '',
        ZIP:        row.zip   || '',
      };
      const data = await searchKnowledgeCore(formData);
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentRow) loadResult(currentRow);
  }, [currentRow, loadResult]);

  const goTo = (idx: number) => {
    if (idx >= 0 && idx < rows.length) setCurrentIndex(idx);
  };

  const searchCriteria: SearchFormData | undefined = currentRow
    ? {
        FIRST_NAME: currentRow.first_name || '',
        LAST_NAME:  currentRow.last_name,
        STREET1:    currentRow.street || '',
        STREET2:    '',
        CITY:       currentRow.city  || '',
        STATE:      currentRow.state || '',
        ZIP:        currentRow.zip   || '',
      }
    : undefined;

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No completed results to display.</Typography>
        <Button onClick={onBack} sx={{ mt: 2 }}>Back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#E5E7EB' }}>

      {/* ── Page Header ── */}
      <Box sx={{
        px: 3,
        py: 2,
        backgroundColor: '#283E56',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        {/* Left: back + view name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={onBack} sx={{ color: 'white' }} size="small">
            <ArrowBack />
          </IconButton>
          <Person sx={{ fontSize: 24, opacity: 0.8 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {[currentRow?.first_name, currentRow?.last_name].filter(Boolean).join(' ')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {viewName}
            </Typography>
          </Box>
        </Box>

        {/* Center: navigator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => goTo(currentIndex - 1)}
            disabled={currentIndex === 0 || loading}
            sx={{ color: 'white', '&:disabled': { color: 'rgba(255,255,255,0.3)' } }}
          >
            <ChevronLeft />
          </IconButton>

          <Box sx={{
            px: 2, py: 0.5, borderRadius: 99,
            backgroundColor: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 60, textAlign: 'center' }}>
              {currentIndex + 1} / {rows.length}
            </Typography>
          </Box>

          <IconButton
            onClick={() => goTo(currentIndex + 1)}
            disabled={currentIndex === rows.length - 1 || loading}
            sx={{ color: 'white', '&:disabled': { color: 'rgba(255,255,255,0.3)' } }}
          >
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Right: address chip */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
          {currentRow?.city && currentRow?.state && (
            <Chip
              label={`${currentRow.city}, ${currentRow.state}`}
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', fontSize: '0.72rem' }}
            />
          )}
        </Box>
      </Box>

      {/* ── Progress bar while loading ── */}
      {loading && (
        <LinearProgress
          sx={{
            height: 3,
            backgroundColor: 'rgba(40,62,86,0.1)',
            '& .MuiLinearProgress-bar': { backgroundColor: '#00C853' },
          }}
        />
      )}

      {/* ── Content ── */}
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && !result && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#283E56' }} />
          </Box>
        )}

        {result && !loading && (
          <TabbedResults data={result} searchCriteria={searchCriteria} />
        )}

        {/* ── Bottom navigation ── */}
        {!loading && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: '1px solid #D1D5DB' }}>
            <Button
              variant="outlined"
              startIcon={<ChevronLeft />}
              onClick={() => goTo(currentIndex - 1)}
              disabled={currentIndex === 0}
              sx={{ borderColor: '#CBD5E0', color: '#283E56', '&:hover': { borderColor: '#283E56' } }}
            >
              Previous
            </Button>

            {/* Dot navigator */}
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              {rows.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => goTo(i)}
                  sx={{
                    width: i === currentIndex ? 20 : 8,
                    height: 8,
                    borderRadius: 99,
                    backgroundColor: i === currentIndex ? '#283E56' : '#D1D5DB',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { backgroundColor: '#283E56' },
                  }}
                />
              ))}
            </Box>

            <Button
              variant="outlined"
              endIcon={<ChevronRight />}
              onClick={() => goTo(currentIndex + 1)}
              disabled={currentIndex === rows.length - 1}
              sx={{ borderColor: '#CBD5E0', color: '#283E56', '&:hover': { borderColor: '#283E56' } }}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EnrichmentResultsPage;
